import React, { useState, useEffect } from "react";
import axios from "axios";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./DettaglioCosti.css";
import Lf23Loader from "../../../components/Lf23Loader";

const DettaglioCosti = ({ filters, loading: parentLoading }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [total, setTotal] = useState(0);

    const [isFullscreenTable, setIsFullscreenTable] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'dataOra', direction: 'desc' });

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };


    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setIsFullscreenTable(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // funzione per formattare numeri in stile italiano
    const formatNumber = (num, decimals = 2) => {
        if (num === null || num === undefined) return "-";
        return parseFloat(num).toLocaleString("it-IT", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    // funzione per formattare data e ora
    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    // funzione per preparare dati Excel da un array (non dallo stato)
    const prepareExcelDataFromArray = (dataArray) => {
        const headers = [
            "Data e Ora",
            "Targa",
            "Prodotto",
            "Volume (L)",
            "Costo (€)",
            "Costo/Litro (€)",
            "Distributore",
            "Gestore",
            "Posizione"
        ];

        const rows = dataArray.map(row => [
            formatDateTime(row.dataOra),
            row.targa || "-",
            row.prodotto || "-",
            parseFloat(row.volume || 0).toFixed(2),
            parseFloat(row.costo || 0).toFixed(2),
            parseFloat(row.costoLitro || 0).toFixed(3),
            row.distributore || "-",
            row.gestore || "-",
            row.posizione || "-"
        ]);

        // Calcola totali
        const totaleVolume = dataArray.reduce((sum, row) => sum + parseFloat(row.volume || 0), 0);
        const totaleCosto = dataArray.reduce((sum, row) => sum + parseFloat(row.costo || 0), 0);
        const costoLitroMedio = totaleVolume > 0 ? totaleCosto / totaleVolume : 0;

        rows.push([
            "TOTALE",
            "-",
            "-",
            totaleVolume.toFixed(2),
            totaleCosto.toFixed(2),
            costoLitroMedio.toFixed(3),
            "-",
            "-",
            "-"
        ]);

        return { headers, rows };
    };


    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();

                if (filters?.fromDate) params.append("fromDate", filters.fromDate);
                if (filters?.toDate) params.append("toDate", filters.toDate);

                // aggiungo gli altri filtri se presenti
                if (filters?.targa?.length > 0) {
                    params.append("targa", filters.targa.map(t => t.value).join(","));
                }
                if (filters?.prodotto?.length > 0) {
                    params.append("prodotto", filters.prodotto.map(p => p.value).join(","));
                }
                if (filters?.distributore?.length > 0) {
                    params.append("distributore", filters.distributore.map(d => d.value).join(","));
                }
                if (filters?.cantiere?.length > 0) {
                    params.append("cantiere", filters.cantiere.map(c => c.value).join(","));
                }
                if (filters?.tipologia?.length > 0) {
                    params.append("tipologia", filters.tipologia.map(t => t.value).join(","));
                }

                //aggiungo parametri paginazione e ordinamento
                params.append("page", page);
                params.append("limit", limit);
                if (sortConfig.key) {
                    params.append("sortBy", sortConfig.key);
                    params.append("sortDir", sortConfig.direction);
                }

                const url = `${API_URL}/trans-automezzi/dettaglio-costi?${params.toString()}`;

                const response = await axios.get(url);
                setData(response.data.rows || []);
                setTotal(response.data.total || 0);

            } catch (error) {
                console.error("Errore nel caricamento dei dati: ", error);
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        if (filters) {
            fetchData();
        }
    }, [filters, page, limit, sortConfig, API_URL]);

    // Reset pagina quando cambiano i filtri o l'ordinamento
    useEffect(() => {
        setPage(1);
    }, [filters?.fromDate, filters?.toDate, filters?.targa, filters?.prodotto,
    filters?.distributore, filters?.cantiere, filters?.tipologia, sortConfig]);

    // Funzione per preparare i dati per l'export Excel
    const prepareExcelData = () => {
        // 1. Definisco le intestazioni (headers) - devono corrispondere alle colonne della tabella
        const headers = [
            "Data e Ora",
            "Targa",
            "Prodotto",
            "Volume (L)",
            "Costo (€)",
            "Costo/Litro (€)",
            "Distributore",
            "Gestore",
            "Posizione"
        ];

        // 2. Trasformo ogni oggetto in un array di valori (rows)
        const rows = data.map(row => [
            formatDateTime(row.dataOra),
            row.targa || "-",
            row.prodotto || "-",
            parseFloat(row.volume || 0).toFixed(2),
            parseFloat(row.costo || 0).toFixed(2),
            parseFloat(row.costoLitro || 0).toFixed(3),
            row.distributore || "-",
            row.gestore || "-",
            row.posizione || "-"
        ]);

        const totaleVolume = data.reduce((sum, row) => sum + parseFloat(row.volume || 0), 0);
        const totaleCosto = data.reduce((sum, row) => sum + parseFloat(row.costo || 0), 0);
        const costoLitroMedio = totaleVolume > 0 ? totaleCosto / totaleVolume : 0;

        // 4. Aggiungo la riga TOTALE
        rows.push([
            "TOTALE",
            "-",
            "-",
            totaleVolume.toFixed(2),
            totaleCosto.toFixed(2),
            costoLitroMedio.toFixed(3),
            "-",
            "-",
            "-"
        ]);

        // 5. Ritorno l'oggetto nel formato corretto
        return { headers, rows };
    };

    // funzione per esportare file excel
    const handleExportExcel = async () => {
        try {
            // Chiamata separata per ottenere TUTTI i dati (senza paginazione)
            const params = new URLSearchParams();

            if (filters?.fromDate) params.append("fromDate", filters.fromDate);
            if (filters?.toDate) params.append("toDate", filters.toDate);

            // Aggiungi gli altri filtri se presenti
            if (filters?.targa?.length > 0) {
                params.append("targa", filters.targa.map(t => t.value).join(","));
            }
            if (filters?.prodotto?.length > 0) {
                params.append("prodotto", filters.prodotto.map(p => p.value).join(","));
            }
            if (filters?.distributore?.length > 0) {
                params.append("distributore", filters.distributore.map(d => d.value).join(","));
            }
            if (filters?.cantiere?.length > 0) {
                params.append("cantiere", filters.cantiere.map(c => c.value).join(","));
            }
            if (filters?.tipologia?.length > 0) {
                params.append("tipologia", filters.tipologia.map(t => t.value).join(","));
            }

            params.append("limit", "999999");
            params.append("page", "1");
            if (sortConfig.key) {
                params.append("sortBy", sortConfig.key);
                params.append("sortDir", sortConfig.direction);
            }

            const url = `${API_URL}/trans-automezzi/dettaglio-costi?${params.toString()}`;
            const response = await axios.get(url);

            const allData = response.data.rows || [];

            // Prepara dati Excel con TUTTI i record
            const excelData = prepareExcelDataFromArray(allData);

            // Chiama API per generare Excel
            const excelResponse = await axios.post(`${API_URL}/trans-automezzi/downloadExcel`, {
                tableName: "Dettaglio costi",
                datiDaStampare: excelData
            },
                { responseType: "blob" }
            );

            // Download
            const downloadUrl = window.URL.createObjectURL(new Blob([excelResponse.data]));
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.setAttribute("download", "dettaglio_costi.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("Errore durante l'export: ", error);
        }
    };


    // gestione stati di visualizzazione
    const isFullLoading = parentLoading;           // loader grande: solo quando cambiano i filtri dal padre
    const isSortLoading = !parentLoading && loading; // overlay leggero: sort o cambio pagina
    const isEmpty = !parentLoading && !loading && data.length === 0;
    const hasData = !parentLoading && data.length > 0;

    return (
        <div className="dettaglio-costi-container">
            {isFullLoading && (
                <Lf23Loader />
            )}

            {!isFullLoading && (
                <div className={`dettaglio-costi-table-section${isFullscreenTable ? ' fullscreen-dettaglio' : ''}`}>
                    <RotateDevice active={isFullscreenTable} />
                    {/* HEADER CON TITOLO E BOTTONE EXPORT */}
                    <div className="dettaglio-costi-table-header">
                        <h3 className="dettaglio-costi-table-title">Dettaglio Costi Carburante</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {hasData && (
                                <button
                                    className="dettaglio-costi-download-btn"
                                    onClick={handleExportExcel}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>
                            )}
                            <button
                                className="dettaglio-costi-fullscreen-btn"
                                onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                                title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenTable ? (
                                        <>
                                            <polyline points="4 14 10 14 10 20"></polyline>
                                            <polyline points="20 10 14 10 14 4"></polyline>
                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    ) : (
                                        <>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>

                    {isEmpty && (
                        <div className="dettaglio-costi-empty">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                            <p>Nessun dato disponibile per i filtri selezionati</p>
                        </div>
                    )}

                    {hasData && (
                        <div className={`dettaglio-costi-table-wrapper${isSortLoading ? ' dettaglio-costi-sort-loading' : ''}`}>
                            <table className="dettaglio-costi-table">
                                <thead>
                                    <tr>
                                        {[
                                            { label: 'Data e Ora', key: 'dataOra' },
                                            { label: 'Targa', key: 'targa' },
                                            { label: 'Prodotto', key: 'prodotto' },
                                            { label: 'Volume (L)', key: 'volume' },
                                            { label: 'Costo (€)', key: 'costo' },
                                            { label: 'Costo/Litro (€)', key: 'costoLitro' },
                                            { label: 'Distributore', key: 'distributore' },
                                            { label: 'Gestore', key: 'gestore' },
                                            { label: 'Posizione', key: 'posizione' },
                                        ].map(({ label, key }) => (
                                            <th key={key} className="dettaglio-costi-th-sortable" onClick={() => handleSort(key)}>
                                                {label}
                                                <span className="dettaglio-costi-sort-icon">
                                                    {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((row, index) => (
                                        <tr key={index}>
                                            <td>{formatDateTime(row.dataOra)}</td>
                                            <td>{row.targa || "-"}</td>
                                            <td>{row.prodotto || "-"}</td>
                                            <td>{formatNumber(row.volume, 2)}</td>
                                            <td>{formatNumber(row.costo, 2)}€</td>
                                            <td>{formatNumber(row.costoLitro, 3)}€</td>
                                            <td>{row.distributore || "-"}</td>
                                            <td>{row.gestore || "-"}</td>
                                            <td>{row.posizione || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}


                    {/* PAGINAZIONE */}
                    {hasData && (
                        <div className="pagination-compact">
                            <button
                                className="pagination-btn-compact"
                                disabled={page === 1}
                                onClick={() => setPage(page - 1)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 18l-6-6 6-6" />
                                </svg>
                                Prec
                            </button>

                            <div className="pagination-info-compact">
                                <span className="pagination-text-compact">
                                    Pag <strong>{page}</strong> di <strong>{Math.ceil(total / limit) || 1}</strong>
                                </span>
                                <span className="pagination-total-compact">({total} risultati totali)</span>
                            </div>

                            <button
                                className="pagination-btn-compact"
                                disabled={page === Math.ceil(total / limit) || total === 0}
                                onClick={() => setPage(page + 1)}
                            >
                                Succ
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>

                            <div className="items-per-page-compact">
                                <span className="items-label-compact">Elementi per pagina:</span>
                                <div className="items-buttons-compact">
                                    {[20, 50, 100, 200].map((num) => (
                                        <button
                                            key={num}
                                            className={`items-btn-compact ${limit === num ? "active" : ""}`}
                                            onClick={() => {
                                                setLimit(num);
                                                setPage(1);
                                            }}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DettaglioCosti;